/** One Teil's item grid — the viobean-style illustrated tile wall.
 *
 * Every tile is FULLY clickable, carries art (the real illustration when
 * `image_url` is set, else the deterministic gradient+emoji fallback from
 * tileArt.ts), and shows its practice status. Filter chips + search keep
 * big banks navigable; the header shows timing and the learner's
 * practiced/mastery summary for this Teil.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as api from "../../api/exams";
import { ApiError } from "../../api/http";
import { ContentLocked } from "../../components/ContentLocked";
import { Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { MonumentIcon } from "./monuments/registry";
import { tileArtFor, tileBackground } from "./tileArt";

type Filter = "all" | "notStarted" | "keepPracticing" | "mastered";

export function itemStatus(item: api.ExamItemCard): Exclude<Filter, "all"> {
  if (!item.practiced) return "notStarted";
  return (item.best_score ?? 0) >= 100 ? "mastered" : "keepPracticing";
}

function ItemTile({ item }: { item: api.ExamItemCard }) {
  const t = useT();
  const status = itemStatus(item);
  const art = tileArtFor(item.slug);
  return (
    <Link
      to={`/practice/items/${item.id}`}
      className="block"
      aria-label={`practice ${item.title}`}
    >
      <Card flush className="h-full transition-all hover:-translate-y-0.5 hover:border-brand">
        <div
          className="grid aspect-[4/3] place-items-center rounded-t-card"
          style={item.image_url ? undefined : { background: tileBackground(art.gradient) }}
        >
          {item.image_url ? (
            <img
              src={item.image_url}
              alt=""
              className="h-full w-full rounded-t-card object-cover"
            />
          ) : (
            <MonumentIcon
              monumentKey={art.monumentKey}
              colorway={art.colorway}
              className="h-full w-full p-3 drop-shadow-[0_5px_7px_rgba(28,24,14,0.42)]"
            />
          )}
        </div>
        <div className="p-4">
          <h3 className="font-bold text-ink">{item.title}</h3>
          <p className="mt-1 text-sm">
            {status === "notStarted" && (
              <span className="italic text-muted">{t("practice.readyToStart")}</span>
            )}
            {status === "keepPracticing" && (
              <span className="font-semibold text-warning">
                {t("practice.best")}: {item.best_score}% · {t("practice.filter.keepPracticing")}
              </span>
            )}
            {status === "mastered" && (
              <span className="font-semibold text-success">✓ {t("practice.filter.mastered")}</span>
            )}
          </p>
        </div>
      </Card>
    </Link>
  );
}

export function PartItemsPage() {
  const t = useT();
  const { board = "", level = "", partCode = "" } = useParams();
  const [data, setData] = useState<api.PartItems | null>(null);
  const [error, setError] = useState(false);
  // 403 from the content gate → the level simply isn't owned yet.
  const [lockedModule, setLockedModule] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    api
      .partItems(board, level, partCode)
      .then((rows) => {
        if (!cancelled) setData(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          // Not a failure — this level just isn't part of their plan.
          setLockedModule(err.requiredModule);
        } else {
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [board, level, partCode]);

  // Any narrowing returns to the first page.
  useEffect(() => setPage(1), [filter, theme, query]);

  const counts = useMemo(() => {
    const base = { all: 0, notStarted: 0, keepPracticing: 0, mastered: 0 };
    for (const item of data?.items ?? []) {
      base.all += 1;
      base[itemStatus(item)] += 1;
    }
    return base;
  }, [data]);

  const themes = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of data?.items ?? []) {
      const tp = item.topic ?? "";
      if (tp) map.set(tp, (map.get(tp) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (data?.items ?? []).filter((item) => {
      if (filter !== "all" && itemStatus(item) !== filter) return false;
      if (theme !== "all" && (item.topic ?? "") !== theme) return false;
      if (!needle) return true;
      return (
        item.title.toLowerCase().includes(needle)
        || (item.topic ?? "").toLowerCase().includes(needle)
      );
    });
  }, [data, filter, theme, query]);

  if (lockedModule !== null) return <ContentLocked module={lockedModule} />;
  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{t("common.error.generic")}</p>
      </Card>
    );
  }
  if (!data) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  const practiced = counts.keepPracticing + counts.mastered;
  const practicedScores = data.items
    .filter((i) => i.practiced && i.best_score !== null)
    .map((i) => i.best_score as number);
  const mastery = practicedScores.length
    ? Math.round(practicedScores.reduce((s, v) => s + v, 0) / practicedScores.length)
    : null;

  const FILTERS: { key: Filter; label: string; count: number }[] = [
    { key: "all", label: t("practice.filter.all"), count: counts.all },
    { key: "notStarted", label: t("practice.filter.notStarted"), count: counts.notStarted },
    { key: "keepPracticing", label: t("practice.filter.keepPracticing"), count: counts.keepPracticing },
    { key: "mastered", label: t("practice.filter.mastered"), count: counts.mastered },
  ];

  const PAGE_SIZE = 12;
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = visible.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/practice" className="text-sm text-muted hover:text-brand">
            ← {t("practice.title")}
          </Link>
          <h1 className="mt-1 text-3xl font-extrabold">{data.part.title}</h1>
          <p className="mt-1 text-muted">{data.part.description}</p>
          <p className="mt-1 text-sm text-muted">
            ⏱ {data.part.timing_minutes} {t("practice.minutes")}
          </p>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <p className="text-2xl font-extrabold text-ink">
              {practiced}/{counts.all}
            </p>
            <p className="text-xs uppercase text-muted">{t("practice.practicedLabel")}</p>
          </div>
          <div>
            <p className="text-2xl font-extrabold text-ink">
              {mastery === null ? "—" : `${mastery}%`}
            </p>
            <p className="text-xs uppercase text-muted">{t("practice.mastery")}</p>
          </div>
        </div>
      </div>

      {data.part.strategy && (
        <details className="rounded-control border border-line bg-canvas p-3">
          <summary className="cursor-pointer text-sm font-semibold text-brand">
            {t("practice.howToApproach")}
          </summary>
          <p className="mt-2 text-sm text-ink">{data.part.strategy}</p>
        </details>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-control border px-3 py-1.5 text-sm font-semibold ${filter === f.key ? "border-brand bg-brand-soft text-brand-dark" : "border-line text-muted hover:text-ink"}`}
            >
              {f.label}
              <span className="ml-1.5 rounded-full bg-line/60 px-1.5 text-xs">{f.count}</span>
            </button>
          ))}
        </div>
        <input
          aria-label={t("practice.search")}
          placeholder={t("practice.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-56 rounded-control border border-line bg-surface px-3 py-1.5 text-sm text-ink"
        />
      </div>

      {themes.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {([["all", 0]] as [string, number][]).concat(themes).map(([name, count]) => {
            const active = theme === name;
            return (
              <button
                key={name}
                onClick={() => setTheme(name)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${active ? "border-brand bg-brand-soft text-brand-dark" : "border-line text-muted hover:text-ink"}`}
              >
                {name === "all" ? t("practice.allThemes") : name}
                {name !== "all" && <span className="ml-1 opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>
      )}

      {visible.length === 0 ? (
        <Card>
          <EmptyState message={t("learn.empty")} />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            {paged.map((item) => (
              <ItemTile key={item.id} item={item} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setPage(current - 1)}
                disabled={current === 1}
                className="rounded-control border border-line px-3 py-1.5 text-sm font-semibold text-muted disabled:opacity-40 enabled:hover:text-ink"
              >
                ← {t("practice.previous")}
              </button>
              <span className="text-sm text-muted">
                {t("practice.pageOf", { page: current, total: totalPages })}
              </span>
              <button
                onClick={() => setPage(current + 1)}
                disabled={current === totalPages}
                className="rounded-control border border-line px-3 py-1.5 text-sm font-semibold text-muted disabled:opacity-40 enabled:hover:text-ink"
              >
                {t("practice.next")} →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
